import {
  TICKET_STATUS_FILTER,
  FORWARD_TO_EXPERTS_TICKET_STATUSES,
  NOMINATED_FOR_REVIEW,
  TICKET_STATUS_ASSIGNED,
  TICKET_STATUS_ESCALATED,
} from 'constants/ticketStatuses';
import isEmpty from 'lodash/isEmpty';
import { getTimeSortFieldFromStatusFilter, shouldUseFifoOrder } from 'util/index';
import { parseSortOption } from 'constants/sortOptions';
import { processAssignedTicket, processNominatedOrReviewTicket } from 'util/dataObjectToStoreObject';
import { CONVERSATION_QUERY_LIMIT } from 'constants/limits';
import { makeGraphQLRequest, makeGraphQLPaginatedRequest } from 'graphql/makeGraphQLRequest';
import keyBy from 'lodash/keyBy';
import zip from 'lodash/zip';
import { LIVE_CHAT_COMMUNICATION_TYPES } from 'constants/conversationTypes';
import {
  GET_ACTIVE_EXPERT_QUEUES_QUERY,
  FIND_CONVERSATIONS_QUERY,
  GET_CONVERSATION_QUERY,
  GET_TAG_HISTORY_AND_COMBINED_TAGS_QUERY,
  NOMINATED_EXPERT_QUEUE,
} from './tickets.gql';
import { getExpertId } from 'util/parseSelectors';
import {
  createEndDateWithTime,
  createEndOfDay,
  createStartDateWithTime,
  createStartOfDay,
  getBrowserTimezone,
} from './dateRangeHelpers';

export const getReviewTickets = async ({ partnersAllowlist = [] } = {}) => {
  const filter = {
    reviewExpertId: {
      eq: getExpertId(),
    },
    reviewStatus: {
      eq: NOMINATED_FOR_REVIEW,
    },
  };

  if (!isEmpty(partnersAllowlist)) {
    filter.and = [{ simplrAccountId: { in: [...partnersAllowlist] } }];
  }

  const paging = {
    first: CONVERSATION_QUERY_LIMIT,
  };

  const sorting = [
    {
      field: 'createdAt',
      direction: 'ASC',
    },
  ];

  const { data: conversations } = await makeGraphQLPaginatedRequest(
    FIND_CONVERSATIONS_QUERY,
    {
      filter,
      sorting,
      paging,
    },
    'conversationsFind'
  );

  return conversations;
};

export const getAllTickets = async ({
  expertId = null,
  ticketStatus,
  businessIdList,
  brandOptions,
  excludeBusinesses,
  skip = 0,
  dateRange,
  simplrTicketNumber = null,
  widgetId = null,
  roomId = null,
  platformTicketId = null,
  partialMatch = false,
  rating,
  communicationTypeFilter = 'all',
  whiteList,
  isPrev,
  cursor,
  partnersAllowlist,
  sortOrder,
}) => {
  const filter = getAllTicketBaseQueryFilter({
    expertId,
    ticketStatus,
    businessIdList,
    brandOptions,
    excludeBusinesses,
    skip,
    dateRange,
    simplrTicketNumber,
    widgetId,
    roomId,
    platformTicketId,
    partialMatch,
    rating,
    communicationTypeFilter,
    whiteList,
    partnersAllowlist,
  });

  const customSort = parseSortOption(sortOrder);
  const sorting = [];
  if (customSort) {
    sorting.push(customSort);
  } else {
    const timeSortField = getTimeSortFieldFromStatusFilter(ticketStatus);
    if (shouldUseFifoOrder(ticketStatus)) {
      sorting.push({ field: timeSortField, direction: 'ASC' });
    } else {
      sorting.push({ field: timeSortField, direction: 'DESC' });
    }
  }

  const paging = isPrev
    ? {
        last: CONVERSATION_QUERY_LIMIT,
        before: cursor,
      }
    : {
        first: CONVERSATION_QUERY_LIMIT,
        after: cursor,
      };

  const { data: conversations, pageInfo } = await makeGraphQLPaginatedRequest(
    FIND_CONVERSATIONS_QUERY,
    {
      filter,
      sorting,
      paging,
    },
    'conversationsFind'
  );

  const conversationIds = conversations.map((c) => c.objectId);

  const expertQueueFilter = {
    isActive: {
      is: true,
    },
    conversationId: {
      in: conversationIds,
    },
  };

  const expertQueuePaging = {
    first: 1000,
  };

  const { data: activeExpertQueues } = await makeGraphQLPaginatedRequest(
    GET_ACTIVE_EXPERT_QUEUES_QUERY,
    {
      filter: expertQueueFilter,
      sorting: [
        {
          field: 'createdAt',
          direction: 'ASC',
        },
      ],
      paging: expertQueuePaging,
    },
    'expertQueuesFind'
  );

  // { someConversationId: someExpertQueueParseObject,... }
  const conversationIdsToExpertQueues = keyBy(activeExpertQueues, (expertQueue) => expertQueue.conversationId);

  const getNominatedExpertId = (conversation) =>
    conversationIdsToExpertQueues[conversation.objectId] &&
    conversationIdsToExpertQueues[conversation.objectId].simplrExpertId;

  const conversationsWithNominatedExperts = zip(conversations, conversations.map(getNominatedExpertId));
  const shouldFilterByNominatedExpert = ticketStatus === TICKET_STATUS_FILTER.nominated && expertId;

  let filteredConversationsWithNominatedExperts;
  if (shouldFilterByNominatedExpert) {
    filteredConversationsWithNominatedExperts = conversationsWithNominatedExperts.filter(
      ([ticket, nominatedExpertId]) => nominatedExpertId === expertId
    );
  } else {
    filteredConversationsWithNominatedExperts = conversationsWithNominatedExperts;
  }

  const processedTickets = filteredConversationsWithNominatedExperts.map(([conversation, _expertId]) =>
    processAssignedTicket(conversation, _expertId)
  );

  // ['someTicketId',...]
  const ticketIds = processedTickets.map((t) => t.ticketId);
  // { someTicketId: { ticketId: 'someTicketId',... } }
  const ticketIdsToTickets = keyBy(processedTickets, (t) => t.ticketId);

  return {
    allTickets: ticketIds,
    ...ticketIdsToTickets,
    hasNextPage: pageInfo.hasNextPage,
    hasPreviousPage: pageInfo.hasPreviousPage,
    nextPageCursor: pageInfo.endCursor,
    previousPageCursor: pageInfo.startCursor,
  };
};

export const getAllTicketBaseQueryFilter = ({
  expertId,
  ticketStatus,
  businessIdList,
  brandOptions = [],
  dateRange,
  simplrTicketNumber = null,
  widgetId = null,
  roomId = null,
  platformTicketId = null,
  partialMatch = false,
  rating,
  communicationTypeFilter = 'all',
  excludeBusinesses,
  whiteList,
  partnersAllowlist,
}) => {
  const filter = {};
  const brandOptionsValues = brandOptions.map((option) => option.value);

  const statusFilter = buildStatusFilter(ticketStatus);
  if (statusFilter) {
    Object.assign(filter, statusFilter);
  }

  const simplrAccountIdAndFilter = [];

  let simplrAccountIdInFilters = [];

  if (!isEmpty(whiteList)) {
    simplrAccountIdInFilters = [...whiteList];
  } else if (!isEmpty(partnersAllowlist)) {
    simplrAccountIdInFilters = [...partnersAllowlist];
  }

  if (businessIdList && businessIdList.length) {
    const simplrAccountList = businessIdList.map((id) => id);
    const brandFilter = {};
    if (excludeBusinesses && brandOptionsValues && isEmpty(brandOptionsValues)) {
      simplrAccountIdAndFilter.push({ simplrAccountId: { notIn: [...simplrAccountList] } });
    } else if (excludeBusinesses && brandOptionsValues && !isEmpty(brandOptionsValues)) {
      simplrAccountIdInFilters = [...simplrAccountList];

      brandFilter.brand = {
        notIn: brandOptionsValues,
      };
    } else if (!excludeBusinesses && brandOptionsValues && isEmpty(brandOptionsValues)) {
      simplrAccountIdInFilters = [...simplrAccountList];
    } else if (!excludeBusinesses && brandOptionsValues && !isEmpty(brandOptionsValues)) {
      simplrAccountIdInFilters = [...simplrAccountList];
      brandFilter.brand = {
        in: brandOptionsValues,
      };
    }
    Object.assign(filter, brandFilter);
  }

  if (simplrAccountIdInFilters.length > 0) {
    simplrAccountIdAndFilter.push({ simplrAccountId: { in: [...simplrAccountIdInFilters] } });
  }

  if (simplrAccountIdAndFilter.length > 0) {
    filter.and = [...simplrAccountIdAndFilter];
  }

  // NOTE(vlad|SPD-15655|2026-08-05): Identifier search is exact `eq` by default. When the user opts
  // into partial match (partialMatch toggle), all identifier fields switch to an anchored prefix
  // `like: "value%"` (index-usable). Exact stays the default because prefix on e.g. the numeric
  // simplrTicketNumber floods results (`_2427` also matches `_24271`, `_242700`, ...) and, with the
  // platform's newest-first-only sort, buries the intended ticket last — so the user chooses when a
  // wide match is what they want. Requires the roomId/platformTicketId indexes (SPD-15232 migration)
  // or prefix scans.
  //
  // Wildcard handling: the data-layer `like` operator is Mongo-regex, not SQL LIKE
  // (comparison.builder.ts: `escapeRegExp(raw).replace(/%/g, '.*')`). Only `%` is a wildcard there;
  // `_` is regex-escaped to a literal, so it must NOT be stripped — underscores are the meaningful
  // base/variant separator in these ids (e.g. `AcmeBase_1001`). We strip `%` so a user can't inject
  // a mid-string wildcard, and return null (skip the filter) if nothing survives the strip, so a
  // wildcard-only input (`%`) can't collapse to a match-all `.*` pattern.
  const buildIdentifierComparison = (value) => {
    if (!partialMatch) {
      return { eq: value };
    }
    const prefix = value.replace(/%/g, '');

    return prefix ? { like: `${prefix}%` } : null;
  };

  if (simplrTicketNumber) {
    const comparison = buildIdentifierComparison(simplrTicketNumber);
    if (comparison) {
      Object.assign(filter, { simplrTicketNumber: comparison });
    }
  }

  if (widgetId) {
    const comparison = buildIdentifierComparison(widgetId);
    if (comparison) {
      // NOTE(vadim|SPD-14094|2026-02-09): For SimplrChat tickets widgetId value is used for a simplrChannel
      Object.assign(filter, { simplrChannel: comparison });
    }
  }

  if (roomId) {
    const comparison = buildIdentifierComparison(roomId);
    if (comparison) {
      Object.assign(filter, { roomId: comparison });
    }
  }

  if (platformTicketId) {
    const comparison = buildIdentifierComparison(platformTicketId);
    if (comparison) {
      Object.assign(filter, { platformTicketId: comparison });
    }
  }

  if (rating) {
    const ratingFilter = {
      rating: {
        eq: rating,
      },
    };

    Object.assign(filter, ratingFilter);
  }

  const communicationTypeFilterFilter = {};
  if (communicationTypeFilter === 'chat') {
    communicationTypeFilterFilter.communicationType = {
      in: [...LIVE_CHAT_COMMUNICATION_TYPES],
    };
    // note that because some tickets do not have a communication type, we cannot just use equalTo('communicationType', 'EMAIL);
    // Since we really only have two communication types, we can assume if the type is not chat then it is really email
    // If we introduce new types we need to fix the bad conversations
  } else if (communicationTypeFilter === 'email') {
    communicationTypeFilterFilter.communicationType = {
      notIn: [...LIVE_CHAT_COMMUNICATION_TYPES],
    };
  }
  if (Object.keys(communicationTypeFilterFilter).length) {
    Object.assign(filter, communicationTypeFilterFilter);
  }

  const timeSortField = getTimeSortFieldFromStatusFilter(ticketStatus);
  const dateRangeFilter = buildDateRangeFilter(dateRange, timeSortField);

  Object.assign(filter, dateRangeFilter);

  const shouldFilterByNominatedExpert = ticketStatus === TICKET_STATUS_FILTER.nominated && expertId;

  // can use query filter if not looking for nominated conversations
  if (!shouldFilterByNominatedExpert && expertId) {
    const simplrExpertFilter = {
      simplrExpertId: {
        eq: expertId,
      },
    };

    Object.assign(filter, simplrExpertFilter);
  }

  return filter;
};

export const getEscalatedTickets = async ({
  businessIdList = [],
  communicationTypeFilter = 'all',
  excludeBusinesses = false,
  whiteList,
  partnersAllowlist,
} = {}) => {
  const filter = getEscalatedTicketsBaseQuery({
    businessIdList,
    communicationTypeFilter,
    excludeBusinesses,
    whiteList,
    partnersAllowlist,
  });
  const sorting = [
    {
      field: 'escalatedAt',
      direction: 'ASC',
    },
  ];

  const paging = {
    first: CONVERSATION_QUERY_LIMIT,
  };

  const { data: escalatedTickets } = await makeGraphQLPaginatedRequest(
    FIND_CONVERSATIONS_QUERY,
    {
      filter,
      sorting,
      paging,
    },
    'conversationsFind'
  );

  const nominatedTickets = { allTickets: [] };
  for (const ticket of escalatedTickets) {
    const processedTicket = processNominatedOrReviewTicket(ticket);
    nominatedTickets[processedTicket.ticketId] = processedTicket;
    nominatedTickets.allTickets.push(processedTicket.ticketId);
  }

  return nominatedTickets;
};

export const getEscalatedTicketsBaseQuery = ({
  businessIdList = [],
  communicationTypeFilter = 'all',
  excludeBusinesses = false,
  whiteList,
  partnersAllowlist,
} = {}) => {
  const filter = {
    status: {
      eq: TICKET_STATUS_ESCALATED,
    },
  };

  const simplrAccountIdAndFilter = [];

  let simplrAccountIdInFilters = [];

  if (!isEmpty(whiteList)) {
    simplrAccountIdInFilters = [...whiteList];
  } else if (!isEmpty(partnersAllowlist)) {
    simplrAccountIdInFilters = [...partnersAllowlist];
  }

  if (businessIdList.length > 0) {
    const simplrAccountList = businessIdList.map((id) => id);
    if (excludeBusinesses) {
      simplrAccountIdAndFilter.push({ simplrAccountId: { notIn: [...simplrAccountList] } });
    } else {
      simplrAccountIdInFilters = [...simplrAccountList];
    }
  }

  if (simplrAccountIdInFilters.length > 0) {
    simplrAccountIdAndFilter.push({ simplrAccountId: { in: [...simplrAccountIdInFilters] } });
  }

  if (simplrAccountIdAndFilter.length > 0) {
    filter.and = [...simplrAccountIdAndFilter];
  }

  const communicationTypeFilterFilter = {};
  if (communicationTypeFilter === 'chat') {
    communicationTypeFilterFilter.communicationType = {
      in: [...LIVE_CHAT_COMMUNICATION_TYPES],
    };
  } else if (communicationTypeFilter === 'email') {
    communicationTypeFilterFilter.communicationType = {
      notIn: [...LIVE_CHAT_COMMUNICATION_TYPES],
    };
  }

  if (Object.keys(communicationTypeFilterFilter).length) {
    Object.assign(filter, communicationTypeFilterFilter);
  }

  return filter;
};

const buildStatusFilter = (ticketStatus) => {
  if (ticketStatus !== TICKET_STATUS_FILTER.all) {
    const ticketStatuses =
      ticketStatus === TICKET_STATUS_FILTER.forwardToExperts ? FORWARD_TO_EXPERTS_TICKET_STATUSES : [ticketStatus];

    return {
      status: {
        in: ticketStatuses,
      },
    };
  }

  return undefined;
};

/**
 * Builds GraphQL date range filter using the browser's timezone.
 * @param {Object} dateRange - Date range object with startDate, endDate, startTime, endTime
 * @param {string} timeSortField - Field name for time-based sorting (e.g., 'createdAt')
 * @returns {Object} GraphQL filter object for date range queries
 */
export const buildDateRangeFilter = (dateRange, timeSortField) => {
  if (!dateRange) {
    return {};
  }

  const { startDate, endDate, startTime, endTime } = dateRange;

  // NOTE(vlad|SPD-14824|2026-05-12): Always use browser timezone — timezone selector removed.
  const timezone = getBrowserTimezone();

  let start = null;

  if (startDate) {
    const baseDate = new Date(startDate);
    start = startTime ? createStartDateWithTime(baseDate, startTime, timezone) : createStartOfDay(baseDate, timezone);
  }

  let end = null;

  if (endDate) {
    const baseDate = new Date(endDate);
    end = endTime ? createEndDateWithTime(baseDate, endTime, timezone) : createEndOfDay(baseDate, timezone);
  }

  let criteria = null;

  if (start && end) {
    criteria = { between: { lower: start, upper: end } };
  } else if (start) {
    criteria = { gte: start };
  } else if (end) {
    criteria = { lte: end };
  }

  if (criteria) {
    return { [timeSortField]: criteria };
  }

  return {};
};

export const getAssignedTicketsHelper = async (
  isAdmin = false,
  shouldGetUnreadTickets = true,
  maxNumTickets = CONVERSATION_QUERY_LIMIT,
  partnersAllowlist = []
) => {
  if (maxNumTickets <= 0) {
    return [];
  }

  const filter = {
    simplrExpertId: {
      eq: getExpertId(),
    },
    status: {
      eq: TICKET_STATUS_ASSIGNED,
    },
    isOpen: {
      is: true,
    },
  };

  if (shouldGetUnreadTickets) {
    filter['unreadCount'] = {
      gte: 1,
    };
  } else {
    filter['unreadCount'] = {
      eq: 0,
    };
  }

  if (!isEmpty(partnersAllowlist)) {
    filter.and = [{ simplrAccountId: { in: [...partnersAllowlist] } }];
  }

  const paging = {
    first: maxNumTickets,
  };

  const sorting = [
    {
      field: 'createdAt',
      direction: 'ASC',
    },
  ];

  const { data: tickets } = await makeGraphQLPaginatedRequest(
    FIND_CONVERSATIONS_QUERY,
    {
      filter,
      sorting,
      paging,
      isAdmin,
    },
    'conversationsFind'
  );

  return tickets;
};

export const getTagHistoryAndCombinedTagsForAConversation = async (conversationId) => {
  return makeGraphQLRequest(GET_TAG_HISTORY_AND_COMBINED_TAGS_QUERY, { conversationId }, 'conversationGet');
};

export const getConversationById = async (conversationId) => {
  return makeGraphQLRequest(GET_CONVERSATION_QUERY, { conversationId }, 'conversationGet');
};

export const nominatedExpertQueue = async (conversationId) => {
  const filter = {
    conversationId: {
      eq: conversationId,
    },
    isActive: {
      is: true,
    },
  };

  return makeGraphQLRequest(NOMINATED_EXPERT_QUEUE, { filter }, 'expertQueueFindFirst');
};
